import { persist } from "zustand/middleware";

import { StoreModel } from "./types";

import {
  AuthSlice,
  createAuthSlice,
  isAuthTokenExpired,
} from "./slices/authSlice";
import { UserSlice, createUserSlice } from "./slices/userSlice";
import { ExamSlice, createExamSlice } from "./slices/examSlice";
import { OrderSlice, createOrderSlice } from "./slices/orderSlice";
import { ScoresheetSlice, createScoresheetSlice } from "./slices/scoresheetSlice";
import {
  EntryWizardDraftSlice,
  createEntryWizardDraftSlice,
} from "./slices/entryWizardSlice";
import {
  NotificationsSlice,
  createNotificationsSlice,
} from "./slices/notificationsSlice";

import { createContext, useContext } from 'react'

import { createStore, StoreApi } from 'zustand'
import { useStoreWithEqualityFn } from 'zustand/traditional'

type State = StoreModel &
  AuthSlice &
  UserSlice &
  NotificationsSlice &
  ExamSlice &
  ScoresheetSlice &
  OrderSlice &
  EntryWizardDraftSlice;

export const store = createStore<State>()(
  persist(
    (set, get, store) => ({
      ...createUserSlice(set, get, store),
      ...createAuthSlice(set, get, store),
      ...createNotificationsSlice(set, get, store),
      ...createExamSlice(set, get, store),
      ...createScoresheetSlice(set, get, store),
      ...createOrderSlice(set, get, store),
      ...createEntryWizardDraftSlice(set, get, store),
      set(partial) {
        set(partial);
      },
    }),
    {
      name: "AppStorage",
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) =>
            [
              "authToken",
              "authTokenIssuedAt",
              "authTokenRememberMe",
              "backToUrl",
              "userId",
              "orderId",
              "entryWizardDrafts",
            ].includes(key)
          )
        ),
    }
  )
);

/**
 * Drop a token that has already outlived the backend session before anything
 * gets a chance to send it. Without this the first request of the session comes
 * back 401 and the global handler kicks the user off whatever screen they
 * opened - including the password-reset link from their inbox.
 *
 * Runs at module scope: `persist` hydrates synchronously from localStorage, so
 * this settles before the first render.
 */
export const pruneExpiredAuthToken = () => {
  const { authToken, authTokenIssuedAt, authTokenRememberMe } =
    store.getState();

  if (isAuthTokenExpired(authToken, authTokenIssuedAt, authTokenRememberMe)) {
    store.setState({
      authToken: undefined,
      authTokenIssuedAt: undefined,
      authTokenRememberMe: undefined,
      backToUrl: undefined,
    });
  }
};

pruneExpiredAuthToken();

const Context = createContext<StoreApi<State>>({} as StoreApi<State>)

export const StoreProvider = ({ children }: { children: React.ReactNode }) => {
  return <Context.Provider value={store}>{children}</Context.Provider>
}

export const useStore = <T,>(selector: (state: State) => T, equalityFn?: (left: T, right: T) => boolean) => {
  const store = useContext(Context)

  if (!store) {
    throw new Error('Context is not provided')
  }

  return useStoreWithEqualityFn(store, selector, equalityFn)
}
